import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Moon, Sun, Globe, Menu, X, ArrowRight } from "lucide-react";
import { useTheme } from "../lib/theme";
import { useI18n } from "../lib/i18n";
import { BRAND } from "../lib/brand";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { cn } from "../lib/utils";

const navItems = (t) => [
  { to: "/", label: t("nav.home"), key: "home" },
  { to: "/services", label: t("nav.services"), key: "services" },
  { to: "/about", label: t("nav.about"), key: "about" },
  { to: "/contact", label: t("nav.contact"), key: "contact" },
];

export const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const { lang, setLang, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const items = navItems(t);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        scrolled
          ? "backdrop-blur-xl bg-background/70 border-b border-border"
          : "bg-transparent"
      )}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="flex h-20 items-center justify-between gap-4">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="nav-logo-link"
          >
            <div className="relative h-10 w-10 overflow-hidden rounded-sm bg-brand-navy ring-1 ring-brand-cyan/30">
              <img
                src={BRAND.logo}
                alt="Mato Systems"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="leading-tight hidden sm:block">
              <div className="font-display text-lg font-extrabold tracking-tight">
                MATO
              </div>
              <div className="font-mono-accent text-[10px] text-muted-foreground">
                SYSTEMS
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
            {items.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.key}`}
                className={({ isActive }) =>
                  cn(
                    "relative px-4 py-2 text-sm font-medium transition-colors",
                    "text-muted-foreground hover:text-foreground",
                    isActive && "text-foreground"
                  )
                }
              >
                {({ isActive }) => (
                  <span className="relative">
                    {item.label}
                    <span
                      className={cn(
                        "absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-brand-cyan transition-transform duration-300",
                        isActive && "scale-x-100"
                      )}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t("common.toggleLang")}
                  data-testid="lang-toggle-btn"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background/50 px-3 py-2 text-xs font-mono-accent hover:border-brand-cyan hover:text-brand-cyan btn-press"
                >
                  <Globe className="h-3.5 w-3.5" />
                  {lang === "en" ? "EN" : "BG"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px]">
                <DropdownMenuItem
                  data-testid="lang-option-en"
                  onClick={() => setLang("en")}
                  className={cn(lang === "en" && "text-brand-cyan")}
                >
                  {t("common.english")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="lang-option-bg"
                  onClick={() => setLang("bg")}
                  className={cn(lang === "bg" && "text-brand-cyan")}
                >
                  {t("common.bulgarian")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              type="button"
              onClick={toggleTheme}
              aria-label={t("common.toggleTheme")}
              data-testid="theme-toggle-btn"
              className="inline-flex h-9 w-9 items-center justify-center rounded-sm border border-border bg-background/50 hover:border-brand-cyan hover:text-brand-cyan btn-press"
            >
              {theme === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>

            <Link to="/contact" className="hidden md:inline-flex" data-testid="nav-cta-btn">
              <Button
                size="sm"
                className="rounded-sm font-mono-accent text-xs bg-brand-navy text-white hover:bg-brand-cyan hover:text-brand-navy dark:bg-brand-cyan dark:text-brand-navy dark:hover:bg-white btn-press"
              >
                {t("nav.cta")}
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </Link>

            <button
              type="button"
              className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-sm border border-border"
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            className="md:hidden pb-6 pt-2 animate-fade-up"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 rounded-sm border border-border bg-background/80 backdrop-blur-xl p-2">
              {items.map((item) => (
                <NavLink
                  key={item.key}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`mobile-nav-link-${item.key}`}
                  className={({ isActive }) =>
                    cn(
                      "px-4 py-3 text-sm font-medium rounded-sm",
                      isActive
                        ? "bg-muted text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link to="/contact" data-testid="mobile-nav-cta">
                <Button className="w-full mt-2 rounded-sm font-mono-accent text-xs bg-brand-navy text-white hover:bg-brand-cyan hover:text-brand-navy dark:bg-brand-cyan dark:text-brand-navy">
                  {t("nav.cta")}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
